<script lang="ts" setup>
import { useField } from 'vee-validate'

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'text',
  },
  label: {
    type: String,
    required: false,
    default: '',
  },
  placeholder: {
    type: String,
    required: false,
    default: '',
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  required: {
    type: Boolean,
    required: false,
    default: false,
  },
  maxLength: {
    type: Number,
    required: false,
    default: 255,
  },
  customClass: {
    type: String,
    default: '',
  },
  customErrorMessage: {
    type: String,
    default: '',
  },
  isTrimSpace: {
    type: Boolean,
    default: false,
  },
})
const { value, errorMessage } = useField(() => props.name)

const trimErrorMessage = computed(() => {
  return errorMessage.value?.trim()
})

const onClear = () => {
  value.value = ''
  emit('onClearErrorMessage', props.name)
}

const slots = useSlots()

const emit = defineEmits(['onInput', 'onFocus', 'onClearErrorMessage'])

watch(value, (newValue) => {
  emit('onInput', newValue)
})
</script>

<template>
  <div
    class="form-input"
    :class="[customClass, { 'form-input--error': customErrorMessage }]"
  >
    <label v-if="props.label" class="form-input__label" :for="props.name"
      >{{ props.label }}
      <span v-if="props.required" class="form-input__required">*</span>
    </label>
    <VTextField
      v-if="type !== 'textarea' && !isTrimSpace"
      v-model="value"
      variant="outlined"
      clearable
      density="compact"
      hide-details
      :type="type"
      :placeholder="props.placeholder"
      :error="Boolean(trimErrorMessage)"
      :disabled="props.disabled"
      :append-inner-icon="disabled ? 'icon-lock' : ''"
      :maxlength="props.maxLength"
      @click:clear="onClear"
      @update:focused="emit('onFocus', $event)"
    >
      <template v-if="slots['prepend-inner']" #prepend-inner>
        <slot name="prepend-inner" />
      </template>
      <template #clear>
        <i class="icon-delete" @click="onClear" />
      </template>
    </VTextField>
    <VTextField
      v-else-if="isTrimSpace"
      v-model.trim="value"
      variant="outlined"
      clearable
      density="compact"
      hide-details
      :type="type"
      :placeholder="props.placeholder"
      :error="Boolean(trimErrorMessage)"
      :disabled="props.disabled"
      :append-inner-icon="disabled ? 'icon-lock' : ''"
      :maxlength="props.maxLength"
      @click:clear="onClear"
      @update:focused="emit('onFocus', $event)"
    >
      <template v-if="slots['prepend-inner']" #prepend-inner>
        <slot name="prepend-inner" />
      </template>
      <template #clear>
        <i class="icon-delete" @click="onClear" />
      </template>
    </VTextField>
    <VTextarea
      v-else
      v-model="value"
      variant="outlined"
      clearable
      density="compact"
      hide-details
      :placeholder="props.placeholder"
      :error="Boolean(trimErrorMessage)"
      :disabled="props.disabled"
      :append-inner-icon="disabled ? 'icon-lock' : ''"
      :maxlength="props.maxLength"
      auto-grow
      :rows="1"
      @click:clear="onClear"
    />
    <span
      v-if="trimErrorMessage || customErrorMessage"
      class="form-input__error-message"
      >{{ trimErrorMessage || customErrorMessage }}</span
    >
  </div>
</template>
